import React from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { Search as SearchIcon, X, AlertCircle } from 'react-feather';
import { Spinner } from 'react-bootstrap';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import Tag from './Tag/Tag';
import './Search.css';
import { TransitionOpacity } from '../../theme/styles';
import { translate } from '../../translations';
import Tooltip from '../Tooltip/Tooltip';
/*
 * <Search /> component accepts props of:
 * showSearchIcon -- show Magnifier Search Icon
 * iconSize -- SearchIcon and X custom sizing
 * iconColor -- SearchIcon and X custom color
 *
 * -- to make search toggleable and hide searchInput, need 2 props below, and need to set showSearchIcon={true}
 * toggleSearchInput={} --set hideSearchInput state = true
 * hideSearchInput={hideSearchInput} --get state.hideSearchInput to hide/show search input
 *
 * onChange={} -- searchValue onChange event
 * onClearInput={} -- clear searchValue onClearInput event
 * value={} -- set value (in this case, from reducer state)
 * placeholder={}
 * placeholderStyle={}
 *
 * multipleRows={} -- determine if search input will be Textarea and expandable
 * disabled={} -- disables the searchbox
 * autoComplete={} -- disable input autoComplete, does not honor "off" or "false", it will, however, skip any field with attribute it doesnt recognize
 * inputClassname={} --for custom classnames
 * inputId={} -- for adding an ID
 *
 * Adding Tags-
 * onPaste          -- function for searchbox Paste
 * onKeyPress,      -- function for searchbox KeyPress (this is here to handle special character manipulation)
 * onBlur,          -- function for searchbox blur
 * onFocus,         -- function for searchbox focus
 * showTags={}      -- enables the use of the tag functionality
 * tagOptions={
 *   {
 *      type: '',           -- the only type that would change anything is 'email', if the type is email, it will be ran through the email validatation function
 *      values: [],         -- values will be an array of tag values
 *      onRemoveTagClicked: -- remove tag function goes here
 *   }
 * }
 *
 * ErrorIcon
 * Allows you to show an optional alert within the search box
 * showErrorIcon = boolean that displays icon in place of searchIcon
 * errorIconSize = size of the alertCircle Icon, defaults to 18
 * errorIconColor = color of the icon, defaults to danger
 * errorIconTooltipText = adds a tooltip with text if present, not shown by default
 */

export class Search extends React.PureComponent {
    renderSearchIcon = () => {
        const {
            toggleSearchInput,
            disabled,
            iconSize,
            iconColor,
        } = this.props;
        return (
            <button className="iconButton" type="button" onClick={toggleSearchInput} disabled={disabled}>
                <SearchIcon className="searchIcon" alt={translate('search_open_icon')} size={iconSize} color={iconColor} />
            </button>
        );
    };

    renderErrorToolTipIcon = () => {
        const {
            errorIconSize = 18,
            errorIconColor = 'var(--dangerText)',
            errorIconTooltipText = '',
        } = this.props;

        const alert = (
            <AlertCircle
                size={errorIconSize}
                color={errorIconColor}
                tabIndex={0}
                alt={translate('search_alert_circle_icon')}
            />
        );

        const alertWithTooltip = (
            <Tooltip content={errorIconTooltipText}>
                {alert}
            </Tooltip>
        );

        return (
            <div className="errorIcon">
                {errorIconTooltipText ? alertWithTooltip : alert}
            </div>
        );
    };

    renderClearSearchIcons = () => {
        const {
            multipleRows,
            isLoading,
            value,
            showClearIcon,
            onClearInput,
            disabled,
            onClearMouseDown,
        } = this.props;

        const clearSearchIconClassname = value || showClearIcon ? 'searchIcon' : 'hidden';
        const clearSearchWrapperClassname = !multipleRows ? 'iconButton' : 'hidden';

        return (
            <div className={clearSearchWrapperClassname} disabled={disabled}>
                {isLoading && <Spinner className="spinnerIcon dropdownLoadingSpinner" animation="border" size="sm" aria-hidden="true" />}
                <X
                    role="button"
                    data-testid="search-close-icon"
                    tabIndex={0}
                    className={clearSearchIconClassname}
                    alt={translate('search_close_icon')}
                    onClick={onClearInput}
                    disabled={disabled}
                    onMouseDown={onClearMouseDown}
                />
            </div>
        );
    };

    renderTextArea = () => {
        const {
            value,
            placeholder,
            placeholderStyle,
            disabled,
            onChange,
            autoComplete,
        } = this.props;

        // TODO: add support for testId
        return (
            <TextareaAutosize
                placeholder={placeholder}
                onChange={onChange}
                className="textAreaInput overlay-scrollbar-thin"
                value={value || ''}
                style={placeholderStyle}
                disabled={disabled}
                autoComplete={autoComplete}
            />
        );
    };

    renderTagOptions = () => {
        const {
            tagOptions,
            disabled,
        } = this.props;
        return (
            <TransitionGroup className="tagSearchTagGroup" component={null}>
                {tagOptions?.values?.map((tagValue) => (
                    <CSSTransition
                        key={tagValue}
                        in
                        classNames={TransitionOpacity}
                        timeout={200}
                    >
                        <Tag
                            tagType={tagOptions.type}
                            tagId={tagValue}
                            value={tagValue}
                            onRemoveTagClicked={tagOptions.onRemoveTagClicked}
                            disabled={disabled}
                        />
                    </CSSTransition>
                ))}
            </TransitionGroup>
        );
    };

    renderSingleInput = () => {
        const {
            value,
            placeholder,
            placeholderStyle,
            disabled,
            autoComplete,
            inputId,
            inputClassName,
            onChange,
            onPaste,
            onKeyPress,
            onBlur,
            onFocus,
            showTags,
            tagOptions,
            testId,
        } = this.props;

        return (
            <input
                id={inputId}
                className={`${inputClassName || ''} searchInput ${showTags && tagOptions?.values?.length > 0 ? 'withTags' : ''}`}
                placeholder={placeholder}
                onChange={onChange}
                type="search"
                value={value || ''}
                style={placeholderStyle}
                disabled={disabled}
                autoComplete={autoComplete}
                onPaste={onPaste}
                onKeyPress={onKeyPress}
                onBlur={onBlur}
                onFocus={onFocus}
                {...testId}
            />
        );
    };

    render() {
        const {
            showSearchIcon,
            includeBorder,
            hideSearchInput,
            multipleRows,
            disabled,
            showTags,
            tagOptions,
            showErrorIcon = false,
        } = this.props;

        const searchContainerClassName = [
            'searchBarContainer',
            includeBorder ? 'includeBorder' : '',
            showErrorIcon ? 'invalid' : '',
            disabled ? 'disabled' : '',
        ].join(' ').trim();

        const searchInputClassName = [
            'searchInputContainer',
            showTags ? 'includeTags overlay-scrollbar-thin' : '',
            showTags && tagOptions?.values?.length > 0 ? 'hasTagValues' : '',
        ].join(' ').trim();

        return (
            <div className="searchBarParentContainer">
                <div className={searchContainerClassName}>
                    {(showSearchIcon && !showErrorIcon) && this.renderSearchIcon()}
                    {showErrorIcon && this.renderErrorToolTipIcon()}
                    {!(showSearchIcon && hideSearchInput) && (
                        <div className={searchInputClassName}>
                            {showTags && this.renderTagOptions()}
                            {multipleRows ? this.renderTextArea() : this.renderSingleInput()}
                            {this.renderClearSearchIcons()}
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default Search;
